/**
 * 1994-beige style kit: injectable base styles for the Windows-3.1/95 look,
 * plus crisp 16x16 pixel-art icons rendered from hand-authored pixmaps.
 * No framework, no deps — plain DOM only.
 */

export type IconName = 'hand' | 'apple' | 'sponge' | 'ball';

const STYLE_ID = 'w94-base-styles';

const BASE_CSS = `
:root {
  --w94-face: #D4D0C8;
  --w94-light: #FFFFFF;
  --w94-dark: #808080;
  --w94-darker: #404040;
  --w94-title-a: #000080;
  --w94-title-b: #1084d0;
  --w94-text: #000;
  --w94-terminal: #33FF66;
  --w94-terminal-bg: #0A0A0A;
}
/* monospace stand-in — real pixel fonts land later */
.w94-font {
  font-family: Consolas, 'Courier New', monospace;
  color: var(--w94-text);
}
.w94-raised {
  background: var(--w94-face);
  border: 2px solid;
  border-color: var(--w94-light) var(--w94-dark) var(--w94-dark) var(--w94-light);
}
.w94-sunken {
  background: var(--w94-face);
  border: 2px solid;
  border-color: var(--w94-dark) var(--w94-light) var(--w94-light) var(--w94-dark);
}
.w94-btn {
  background: var(--w94-face);
  border: 2px solid;
  border-color: var(--w94-light) var(--w94-dark) var(--w94-dark) var(--w94-light);
  font: bold 11px Consolas, 'Courier New', monospace;
  color: #000;
  image-rendering: pixelated;
  display: grid;
  place-items: center;
  padding: 0;
  margin: 0;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.w94-btn.w94-armed,
.w94-btn:active {
  border-color: var(--w94-dark) var(--w94-light) var(--w94-light) var(--w94-dark);
}
@keyframes w94-pulse {
  0%, 100% { transform: scale(1); filter: brightness(1); }
  50% { transform: scale(1.07); filter: brightness(1.18); }
}
.w94-pulse {
  animation: w94-pulse 1.25s ease-in-out infinite;
}
`;

/** Idempotent: injects the base <style> tag once, keyed by element id. */
export function injectBaseStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = BASE_CSS;
  document.head.appendChild(style);
}

interface Pixmap {
  readonly palette: Readonly<Record<string, string>>;
  readonly rows: readonly string[];
}

// 16x16 pixmaps: one string per row, one char per pixel, '.' = transparent.
const PIXMAPS: Record<IconName, Pixmap> = {
  // Pointing hand: index finger up, folded fist, thumb out left.
  hand: {
    palette: { o: '#20160E', s: '#F0C08C', d: '#C69258' },
    rows: [
      '......oo........',
      '.....osso.......',
      '.....osso.......',
      '.....osso.......',
      '.....ossooo.....',
      '.....ossosso....',
      '.....ossossoo...',
      '..oo.ossosssso..',
      '.ossoossssssso..',
      '.osssossssssso..',
      '..osssssssssso..',
      '..osssssssssdo..',
      '...ossssssssdo..',
      '...odddddddddo..',
      '....ooooooooo...',
      '................',
    ],
  },
  // Red apple: brown stem, green leaf, white glint, shaded base.
  apple: {
    palette: {
      o: '#3D1518',
      r: '#DC2A1E',
      w: '#FFCDBE',
      d: '#9E1A10',
      t: '#7B4A21',
      g: '#3E9C3A',
    },
    rows: [
      '........t.......',
      '........t.ggg...',
      '.......tt.ggggg.',
      '.......t..ggg...',
      '...ooootooooo...',
      '..orrrrrrrrrro..',
      '.orwwrrrrrrrrro.',
      '.orwrrrrrrrrrro.',
      '.orrrrrrrrrrrro.',
      '.orrrrrrrrrrrro.',
      '.orrrrrrrrrrrro.',
      '..orrrrrrrrddo..',
      '...orrrrrrddo...',
      '....oorrddoo....',
      '......oooo......',
      '................',
    ],
  },
  // Yellow-tan block with darker holes and blue foam bubbles on top.
  sponge: {
    palette: {
      o: '#6B5322',
      y: '#E6C464',
      h: '#B08E38',
      b: '#66BBEE',
      B: '#CFEBFA',
    },
    rows: [
      '................',
      '...bb....b......',
      '..bBBb..bBb.bb..',
      '..oooooooooooo..',
      '.oyyyyyyyyyyyyo.',
      '.oyyhhyyyyyyyyo.',
      '.oyyhhyyyhhyyyo.',
      '.oyyyyyyyhhyyyo.',
      '.oyyyyyhyyyyyyo.',
      '.oyhhyyyyyyyyyo.',
      '.oyhhyyyyyhhyyo.',
      '.oyyyyyyyyhhyyo.',
      '.oyyyyhhyyyyyyo.',
      '.oyyyyyyyyyyyyo.',
      '..oooooooooooo..',
      '................',
    ],
  },
  // Blue-and-white beach ball: white meridian band, light glint upper left.
  ball: {
    palette: { o: '#1C2E4A', b: '#2E6FD0', w: '#F4F8FF', l: '#8FC3F5' },
    rows: [
      '.....oooooo.....',
      '...oobbwwbboo...',
      '..obbbwwwwbbbo..',
      '.obllbwwwwbbbbo.',
      '.obllwwwwwwbbbo.',
      'obbbbwwwwwwbbbbo',
      'obbbwwwwwwwwbbbo',
      'obbbwwwwwwwwbbbo',
      'obbbwwwwwwwwbbbo',
      'obbbwwwwwwwwbbbo',
      'obbbbwwwwwwbbbbo',
      '.obbbwwwwwwbbbo.',
      '.obbbbwwwwbbbbo.',
      '..obbbwwwwbbbo..',
      '...oobbwwbboo...',
      '.....oooooo.....',
    ],
  },
};

/**
 * Renders a named 16x16 pixmap to a canvas at an integer scale (default 3,
 * i.e. 48x48 device pixels), nearest-neighbour crisp.
 */
export function pixelIcon(name: IconName, scale = 3): HTMLCanvasElement {
  const { palette, rows } = PIXMAPS[name];
  const s = Math.max(1, Math.floor(scale));
  const canvas = document.createElement('canvas');
  canvas.width = 16 * s;
  canvas.height = 16 * s;
  canvas.style.imageRendering = 'pixelated';
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.imageSmoothingEnabled = false;
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y];
    if (!row) continue;
    for (let x = 0; x < row.length; x++) {
      const color = palette[row.charAt(x)];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x * s, y * s, s, s);
    }
  }
  return canvas;
}
